import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from 'app/store';
import {IProcess, IProcessMapNode, IPhase, IStep, ZoomActions, ProcessTabs} from 'app/types/Shared';
// import {omApi} from '../operatingModel/operatingModelApi';

export enum OnboardingActions {
  DisableOnboardingModal = 'DISABLE_ONBOARDING_MODAL',
  EnableOnboardingModal = 'ENABLE_ONBOARDING_MODAL',
  DisableTopBanner = 'DISABLE_TOP_BANNER',
  EnableTopBanner = 'ENABLE_TOP_BANNER',
  DisablePageBanner = 'DISABLE_PAGE_BANNER',
  EnablePageBanner = 'ENABLE_PAGE_BANNER',
  DisableRoleGuide = 'DISABLE_ROLE_GUIDE',
  EnableRoleGuide = 'ENABLE_ROLE_GUIDE',
  DisableHelpGuide = 'DISABLE_HELP_GUIDE',
  EnableHelpGuide = 'ENABLE_HELP_GUIDE',
  DisableAll = 'DISABLE_ALL',
  EnableAll = 'ENABLE_ALL',
}

type onboardingState = {
  onboardingModal: boolean;
  topBanner: boolean;
  pageBanner: boolean;
  roleGuide: boolean;
  helpGuide: boolean;
};

interface AppState {
  selectedPhase?: IPhase;
  selectedProcess?: IProcess;
  selectedRoleFilters: Array<string>;
  selectedProcessSteps?: Array<IStep>; //Steps for selected process for raci view
  selectedProcessMapNodes?: Array<IProcessMapNode>;
  subProcess: number; //for data filtering on proces map (which L1 selected)
  mapZoom: number;
  defaultZoom: number;
  currentMapXL: boolean;
  processPageTab: ProcessTabs; //Current Tab Selected
  processPageTabIndex: number; //Current Tab Index
  SelectedL1Label: string; //L1 header selected
  selectedL2Label: string; //L2 header selected
  onboarding: onboardingState;
  oneYearBannerOpen?: boolean;
  selectedGroup: string;
  selectedParentE2E: Array<number>;
  simplifiedRACI: boolean;
  roleOrganization: string;
  l2Reset?: boolean;
  selectedReleases: Array<string>;
  hiddenChanges: Array<number>;
  newProcessesPopup?: boolean;
  processModelSelections: Array<{process_name: string; model: string}>;
  inactivityLogout?: boolean; // Used to track if the user has been inactive for a certain period
  lastVisitedUrl?: string; // Track the last visited URL
}

const initialState: AppState = {
  selectedPhase: undefined,
  selectedProcess: undefined,
  selectedRoleFilters: [],
  selectedProcessSteps: [],
  selectedProcessMapNodes: [],
  processPageTab: ProcessTabs.Map,
  processPageTabIndex: 1,
  subProcess: 1,
  SelectedL1Label: '',
  selectedL2Label: '',
  mapZoom: 0.8,
  defaultZoom: 0.6,
  currentMapXL: false,
  onboarding: {
    onboardingModal: true,
    topBanner: true,
    pageBanner: true,
    roleGuide: true,
    helpGuide: true,
  },
  selectedGroup: '',
  selectedParentE2E: [],
  simplifiedRACI: false,
  roleOrganization: '',
  l2Reset: true,
  selectedReleases: [],
  hiddenChanges: [],
  newProcessesPopup: true,
  processModelSelections: [],
  oneYearBannerOpen: false,
  inactivityLogout: false, // Initialize inactivity logout state
  lastVisitedUrl: undefined, // Initialize last visited URL
};

const appStateSlice = createSlice({
  name: 'appState',
  initialState,
  reducers: {
    setSelectedProcess: (state, {payload}: PayloadAction<IProcess>) => {
      state.selectedProcess = payload;
    },
    setSelectedProcessSteps: (state, {payload}: PayloadAction<Array<IStep>>) => {
      state.selectedProcessSteps = payload;
    },
    setSelectedProcessMapNodes: (state, {payload}: PayloadAction<Array<IProcessMapNode>>) => {
      state.selectedProcessMapNodes = payload;
    },
    setSelectedPhase: (state, {payload}: PayloadAction<IPhase>) => {
      state.selectedPhase = payload;
    },
    setSelectedParentOpen: (state, {payload}: PayloadAction<number>) => {
      if (state.selectedParentE2E.includes(payload)) {
        state.selectedParentE2E = state.selectedParentE2E.filter((x) => x !== payload);
      } else {
        state.selectedParentE2E.push(payload);
      }
    },
    setSelectedRoleFilters: (state, {payload}: PayloadAction<Array<string>>) => {
      state.selectedRoleFilters = payload;
    },
    setSelectedSubProcess: (state, {payload}: PayloadAction<number>) => {
      state.subProcess = payload;
    },
    setMapZoom: (state, {payload}: PayloadAction<ZoomActions>) => {
      switch (payload) {
        case ZoomActions.ZoomIn: {
          state.mapZoom += 0.2;
          break;
        }
        case ZoomActions.ZoomOut: {
          state.mapZoom -= 0.2;
          break;
        }
        default:
          break;
      }
    },
    initDefaultZoom: (state, {payload}: PayloadAction<number>) => {
      state.defaultZoom = payload;
    },
    setCurrentMapXL: (state, {payload}: PayloadAction<boolean>) => {
      state.currentMapXL = payload;
    },
    setDefaultZoom: (state, {payload}: PayloadAction<ZoomActions>) => {
      switch (payload) {
        case ZoomActions.ZoomIn: {
          state.defaultZoom += 0.2;
          break;
        }
        case ZoomActions.ZoomOut: {
          state.defaultZoom -= 0.2;
          break;
        }
        default:
          break;
      }
    },
    setProcessPageTab: (state, {payload}: PayloadAction<ProcessTabs>) => {
      state.processPageTab = payload;
    },
    setProcessPageTabIndex: (state, {payload}: PayloadAction<number>) => {
      state.processPageTabIndex = payload;
    },

    setSelectedL1Label: (state, {payload}: PayloadAction<string>) => {
      state.SelectedL1Label = payload;
    },
    setSelectedL2Label: (state, {payload}: PayloadAction<string>) => {
      state.selectedL2Label = payload;
    },
    setOnboarding: (state, {payload}: PayloadAction<OnboardingActions>) => {
      switch (payload) {
        case OnboardingActions.DisableOnboardingModal: {
          state.onboarding.onboardingModal = false;
          break;
        }
        case OnboardingActions.EnableOnboardingModal: {
          state.onboarding.onboardingModal = true;
          break;
        }
        case OnboardingActions.DisableTopBanner: {
          state.onboarding.topBanner = false;
          break;
        }
        case OnboardingActions.EnableTopBanner: {
          state.onboarding.topBanner = true;
          break;
        }
        case OnboardingActions.DisablePageBanner: {
          state.onboarding.pageBanner = false;
          break;
        }
        case OnboardingActions.EnablePageBanner: {
          state.onboarding.pageBanner = true;
          break;
        }
        case OnboardingActions.DisableRoleGuide: {
          state.onboarding.roleGuide = false;
          break;
        }
        case OnboardingActions.EnableRoleGuide: {
          state.onboarding.roleGuide = true;
          break;
        }
        case OnboardingActions.DisableHelpGuide: {
          state.onboarding.helpGuide = false;
          break;
        }
        case OnboardingActions.EnableHelpGuide: {
          state.onboarding.helpGuide = true;
          break;
        }
        case OnboardingActions.DisableAll: {
          state.onboarding = {
            onboardingModal: false,
            topBanner: false,
            pageBanner: false,
            roleGuide: false,
            helpGuide: false,
          };
          break;
        }
        case OnboardingActions.EnableAll: {
          state.onboarding = {
            onboardingModal: true,
            topBanner: true,
            pageBanner: true,
            roleGuide: true,
            helpGuide: true,
          };
          break;
        }

        default:
          break;
      }
    },
    setSelectedGroup: (state, {payload}: PayloadAction<string>) => {
      state.selectedGroup = payload;
    },
    setSimplifiedRACI: (state, {payload}: PayloadAction<boolean>) => {
      state.simplifiedRACI = payload;
    },
    setL2Reset: (state, {payload}: PayloadAction<boolean>) => {
      state.l2Reset = payload;
    },
    setRoleOrganization: (state, {payload}: PayloadAction<string>) => {
      state.roleOrganization = payload;
    },
    setSelectedReleases: (state, {payload}: PayloadAction<Array<string>>) => {
      state.selectedReleases = payload;
    },
    setHiddenChanges: (state, {payload}: PayloadAction<Array<number>>) => {
      state.hiddenChanges = payload;
    },
    resetHiddenChanges: (state) => {
      state.hiddenChanges = [];
    },
    setNewProcessPopup: (state, {payload}: PayloadAction<boolean>) => {
      state.newProcessesPopup = payload;
    },
    setOneYearBannerOpen: (state, {payload}: PayloadAction<boolean>) => {
      state.oneYearBannerOpen = payload;
    },
    setProcessModelSelections: (state, {payload}: PayloadAction<{process_name: string; model: string}>) => {
      if (state.processModelSelections?.map((x) => x.process_name).includes(payload.process_name)) {
        state.processModelSelections = state.processModelSelections.filter(
          (p) => p.process_name !== payload.process_name,
        );
      }
      state.processModelSelections?.push(payload);
    },
    setInactivityLogout: (state, {payload}: PayloadAction<boolean>) => {
      state.inactivityLogout = payload;
    },
    resetInactivityLogout: (state) => {
      state.inactivityLogout = false;
    },
    setLastVisitedUrl: (state, {payload}: PayloadAction<string>) => {
      state.lastVisitedUrl = payload;
    },
    resetAppState: () => {
      return initialState;
    },
  },
});

export const {
  setSelectedProcess,
  setSelectedProcessSteps,
  setSelectedProcessMapNodes,
  setSelectedPhase,
  setSelectedRoleFilters,
  setSelectedSubProcess,
  setSelectedL1Label,
  setSelectedL2Label,
  setOnboarding,
  setSelectedGroup,
  setSelectedParentOpen,
  setMapZoom,
  setProcessPageTab,
  setProcessPageTabIndex,
  setSimplifiedRACI,
  setRoleOrganization,
  setL2Reset,
  setHiddenChanges,
  setNewProcessPopup,
  setSelectedReleases,
  resetHiddenChanges,
  setProcessModelSelections,
  setDefaultZoom,
  initDefaultZoom,
  setCurrentMapXL,
  setOneYearBannerOpen,
  resetInactivityLogout,
  setLastVisitedUrl,
  resetAppState,
} = appStateSlice.actions;

export const selectRoleFilters = (state: RootState) => state.appState.selectedRoleFilters;
export const selectSelectedPhase = (state: RootState) => state.appState.selectedPhase;
export const selectSelectedProcess = (state: RootState) => state.appState.selectedProcess;
export const selectSelectedProcessSteps = (state: RootState) => state.appState.selectedProcessSteps;
export const selectSelectedProcessMapNodes = (state: RootState) => state.appState.selectedProcessMapNodes;
export const selectSubProcess = (state: RootState) => state.appState.subProcess;
export const selectSelectedL1Label = (state: RootState) => state.appState.SelectedL1Label;
export const selectL2Label = (state: RootState) => state.appState.selectedL2Label;
export const selectMapZoom = (state: RootState) => state.appState.mapZoom;
export const selectDefaultZoom = (state: RootState) => state.appState.defaultZoom;
export const selectCurrentMapXL = (state: RootState) => state.appState.currentMapXL;
export const selectProcessPageTab = (state: RootState) => state.appState.processPageTab;
export const selectProcessPageTabIndex = (state: RootState) => state.appState.processPageTabIndex;
export const selectParentOpen = (state: RootState) => state.appState.selectedParentE2E;
export const selectOnboarding = (state: RootState) => state.appState.onboarding;
export const selectSelectedGroup = (state: RootState) => state.appState.selectedGroup;
export const selectSimplifiedRACI = (state: RootState) => state.appState.simplifiedRACI;
export const selectRoleOrganization = (state: RootState) => state.appState.roleOrganization;
export const selectL2Reset = (state: RootState) => state.appState.l2Reset;
export const selectSelectedReleases = (state: RootState) => state.appState.selectedReleases;
export const selectHiddenChanges = (state: RootState) => state.appState.hiddenChanges;
export const selectNewProcessPopup = (state: RootState) => state.appState.newProcessesPopup;
export const selectOneYearBannerOpen = (state: RootState) => state.appState.oneYearBannerOpen;
export const selectProcessModelSelections = (state: RootState) => state.appState.processModelSelections;
export const selectInactivityLogout = (state: RootState) => state.appState.inactivityLogout;
export const selectLastVisitedUrl = (state: RootState) => state.appState.lastVisitedUrl;
export default appStateSlice.reducer;
